import React, { useCallback, useEffect, useState } from "react";
import { Loader2, X, FileText, MessagesSquare, Send, Eye, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDateTime } from "../../lib/ist";
import ModalPortal from "../../components/ModalPortal";
import { MiniMarkdown } from "../../lib/miniMarkdown";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>
);

function OutputModal({ output, onClose }) {
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
        <div className="w-full max-w-3xl bg-white rounded-2xl p-6 my-6" onClick={e => e.stopPropagation()} data-testid="aiwork-output-modal">
          <div className="flex justify-between items-start">
            <div>
              <div className="font-mono text-xs text-emerald-700">{output.project_number} · {output.type} · agent: {output.agent}</div>
              <h3 className="font-bold text-slate-900">{output.title}</h3>
              <div className="text-xs text-slate-500 mt-0.5">{output.customer_email} · {fmtDateTime(output.created_at)} · requested by {output.requested_by}</div>
            </div>
            <button onClick={onClose}><X size={18} className="text-slate-400" /></button>
          </div>
          <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-xl bg-slate-50 border border-slate-100 p-4">
            <MiniMarkdown content={output.content} light />
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Outputs() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(null);
  const load = useCallback(() => { api.get("/admin/ai-work/outputs").then(r => setRows(r.data.rows)).catch(() => toast.error("Failed to load outputs")); }, []);
  useEffect(load, [load]);
  const setStatus = async (o, status) => {
    try {
      await api.patch(`/admin/ai-work/outputs/${o.id}`, { status });
      toast.success(status === "delivered" ? "Delivered — now visible in the customer's dashboard" : "Reverted to draft");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!rows) return <Loader2 className="animate-spin mx-auto my-10 text-emerald-600" />;
  if (rows.length === 0) return <Card className="p-8 text-center text-sm text-slate-400"><FileText size={24} className="mx-auto mb-2 text-slate-300" />No AI deliverables yet. Open a project in the Projects tab and hit "AI Execute".</Card>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="aiwork-outputs-table">
        <thead className="text-left text-slate-400 border-b border-slate-100">
          <tr><th className="px-3 py-2.5">Created</th><th className="px-2">Project</th><th className="px-2">Customer</th><th className="px-2">Deliverable</th><th className="px-2">Status</th><th className="px-2">Actions</th></tr>
        </thead>
        <tbody>
          {rows.map(o => (
            <tr key={o.id} className="border-b border-slate-50">
              <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{fmtDateTime(o.created_at)}</td>
              <td className="px-2 font-mono text-emerald-700 whitespace-nowrap">{o.project_number}</td>
              <td className="px-2 text-slate-600">{o.customer_email}</td>
              <td className="px-2 text-slate-800 font-semibold max-w-[280px] truncate">{o.title}</td>
              <td className="px-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${o.status === "delivered" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
              </td>
              <td className="px-2 whitespace-nowrap">
                <button onClick={() => setView(o)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="View content" data-testid={`aiwork-view-${o.id.slice(0, 6)}`}><Eye size={14} /></button>
                {o.status === "draft" ? (
                  <button onClick={() => setStatus(o, "delivered")} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Deliver to customer" data-testid={`aiwork-deliver-${o.id.slice(0, 6)}`}><Send size={14} /></button>
                ) : (
                  <button onClick={() => setStatus(o, "draft")} className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600" title="Revert to draft" data-testid={`aiwork-revert-${o.id.slice(0, 6)}`}><RotateCcw size={14} /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {view && <OutputModal output={view} onClose={() => setView(null)} />}
    </Card>
  );
}

function ChatLogs() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/ai-work/chats?limit=200").then(r => setRows(r.data.rows)).catch(() => toast.error("Failed to load chats")); }, []);
  if (!rows) return <Loader2 className="animate-spin mx-auto my-10 text-emerald-600" />;
  if (rows.length === 0) return <Card className="p-8 text-center text-sm text-slate-400"><MessagesSquare size={24} className="mx-auto mb-2 text-slate-300" />No customer AI chats yet.</Card>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="aiwork-chats-table">
        <thead className="text-left text-slate-400 border-b border-slate-100">
          <tr><th className="px-3 py-2.5">When</th><th className="px-2">Customer</th><th className="px-2">Dept</th><th className="px-2">Role</th><th className="px-2">Message</th></tr>
        </thead>
        <tbody>
          {rows.map(m => (
            <tr key={m.id} className="border-b border-slate-50">
              <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{fmtDateTime(m.at)}</td>
              <td className="px-2 text-slate-600">{m.user_email}</td>
              <td className="px-2"><span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{m.department}</span></td>
              <td className="px-2">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.role === "user" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{m.role === "user" ? "Customer" : "AI"}</span>
              </td>
              <td className="px-2 text-slate-600 max-w-[420px]">
                <span className="line-clamp-2">{m.content}</span>
                {m.action && <span className="block text-[10px] font-bold text-emerald-600 mt-0.5">→ Task created: {m.action.title}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function AdminAIWork() {
  const [sub, setSub] = useState("outputs");
  return (
    <div data-testid="admin-aiwork">
      <div className="flex gap-1.5 mb-3">
        <button onClick={() => setSub("outputs")} className={`px-3 py-2 rounded-lg text-xs font-bold border inline-flex items-center gap-1.5 ${sub === "outputs" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500"}`} data-testid="aiwork-subtab-outputs"><FileText size={13} /> AI Deliverables</button>
        <button onClick={() => setSub("chats")} className={`px-3 py-2 rounded-lg text-xs font-bold border inline-flex items-center gap-1.5 ${sub === "chats" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-200 text-slate-500"}`} data-testid="aiwork-subtab-chats"><MessagesSquare size={13} /> Customer Chat Logs</button>
      </div>
      {sub === "outputs" ? <Outputs /> : <ChatLogs />}
    </div>
  );
}
